import React, { useEffect, useMemo } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Divider, Form, message } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';

import {
  IOwnerPhoneNotificationSettings,
  ownerPhoneNotificationsSettingsUpdate,
  useOwnerPhoneNotificationSettings,
} from 'api/owner_sdk';
import { Button, Checkbox, IconButton, Link } from 'ui-kit';
import Wrapper from 'components/Wrapper';
import InputPhoneMask from 'components/Form/InputPhoneMask';

import {
  EMPTY_PHONE_NUMBER_REGEX,
  formatPhoneNumber,
  PHONE_NUMBER_REGEX,
  validateAndFormatPhoneNumber,
} from 'components/Form/InputPhoneMask/utils';
import './styles.scss';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { PaymentPlan } from 'api/types/owner';

const emptyPhoneNumber: IOwnerPhoneNotificationSettings = {
  id: null,
  phone_number: '',
  is_request_sms_enabled: false,
  is_sales_sms_enabled: false,
  is_marketing_sms_enabled: false,
};

const validSchema = yup.object({
  phone_notification_settings: yup.array().of(
    yup.object({
      phone_number: yup
        .string()
        .test('valid-phone-number', 'Please input a valid phone number.', (value) => {
          if (
            value.match(EMPTY_PHONE_NUMBER_REGEX) ||
            !_.isNull(validateAndFormatPhoneNumber({ phoneNumber: value }))
          ) {
            return true;
          }

          return PHONE_NUMBER_REGEX.test(value);
        }),
    }),
  ),
});

export interface IAccountNotificationForm {
  ownerPlan: PaymentPlan;
}

const AccountNotificationForm = ({ ownerPlan }: IAccountNotificationForm) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const {
    data: phoneNotificationSettings,
    isLoading,
    mutate: updateSettings,
    error,
  } = useOwnerPhoneNotificationSettings();

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  const disabled = useMemo(() => ownerPlan === PaymentPlan.Basic, [ownerPlan]);

  const disabledAdditionalPhones = useMemo(
    () => _.includes([PaymentPlan.Basic, PaymentPlan.Pro], ownerPlan),
    [ownerPlan],
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
  } = useForm({
    mode: 'all',
    defaultValues: {
      phone_notification_settings: [emptyPhoneNumber],
    },
    resolver: yupResolver(validSchema),
  });

  const {
    fields,
    append: appendPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({
    control,
    name: 'phone_notification_settings',
  });

  useEffect(() => {
    if (phoneNotificationSettings) {
      if (_.isEmpty(phoneNotificationSettings)) {
        reset({
          phone_notification_settings: [emptyPhoneNumber],
        });
      } else {
        reset({ phone_notification_settings: phoneNotificationSettings });
      }
    }
  }, [phoneNotificationSettings]);

  const onSubmit = (data: {
    phone_notification_settings: Array<IOwnerPhoneNotificationSettings>;
  }) => {
    const hasMissingData =
      data.phone_notification_settings.filter(
        (item) =>
          !_.isEmpty(item.phone_number) &&
          !item.is_sales_sms_enabled &&
          !item.is_request_sms_enabled &&
          !item.is_marketing_sms_enabled,
      ).length > 0;

    if (hasMissingData) {
      message.error('Select the text info you would like to receive.');
      return;
    }

    const hasMissingPhoneNumber =
      data.phone_notification_settings.filter(
        (item) =>
          _.isEmpty(item.phone_number) &&
          (item.is_request_sms_enabled ||
            item.is_sales_sms_enabled ||
            item.is_marketing_sms_enabled),
      ).length > 0;

    if (hasMissingPhoneNumber) {
      message.error('Phone number is required to receive text info.');
      return;
    }
    showLoading();

    const formattedDataArray = data.phone_notification_settings.map((item) => ({
      ...item,
      phone_number: formatPhoneNumber({ phone: item.phone_number }),
    }));

    ownerPhoneNotificationsSettingsUpdate({ phoneNotificationSettings: formattedDataArray })
      .then(() => {
        message.success('Successfully saved account notification preferences.');
        updateSettings();

        hideLoading();
      })
      .catch((e) => {
        message.error(e.message);
        hideLoading();
      });
  };

  return (
    <>
      {!isLoading && (
        <Wrapper className="hc-settings notifications">
          <Form
            name="settings-notifications"
            layout="vertical"
            initialValues={phoneNotificationSettings}
            style={{ marginBottom: '16px' }}
          >
            {fields.map((field, index) => (
              <div
                key={field.id}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <div>
                  <Controller
                    control={control}
                    name={`phone_notification_settings.${index}.phone_number`}
                    render={({
                      field: { name, value, onChange, onBlur },
                      fieldState: { error },
                    }) => {
                      return (
                        <Form.Item
                          name={name}
                          label="Phone number"
                          className="phone-number-label"
                          tooltip={{
                            title: 'Available in the US and Canada.',
                            icon: <ExclamationCircleOutlined />,
                          }}
                          validateStatus={error ? 'error' : ''}
                          help={error ? error?.message : null}
                        >
                          <input value={value} type="hidden" />
                          <InputPhoneMask
                            onBlur={onBlur}
                            onChange={onChange}
                            value={value}
                            disabled={disabled}
                          />
                        </Form.Item>
                      );
                    }}
                  />
                  <div className="list_notifications">
                    <p className="label-title">Enable notifications for:</p>
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_request_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            Request and Approve: Request, Approve, Deny, Cancelled, Expired
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_sales_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            Sales: Purchase, Cancellation, Service Reminders, Inventory
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_marketing_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            News and Feature Updates
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    {disabled && (
                      <p className="upgrade-txt">
                        * <Link href={`/pricing/`}>Upgrade to Pro</Link> for text messaging
                      </p>
                    )}
                  </div>
                  <Divider />
                </div>
                {!disabled && (
                  <div>
                    <IconButton onClick={() => removePhoneNumber(index)}>
                      <DeleteOutlined />
                    </IconButton>
                  </div>
                )}
              </div>
            ))}
            <Button
              disabled={disabledAdditionalPhones}
              variant="outlined"
              onClick={() => appendPhoneNumber(emptyPhoneNumber)}
              startIcon={<PlusOutlined />}
            >
              Add more
            </Button>
            {disabledAdditionalPhones && (
              <p className="upgrade-txt">
                * <Link href={`/pricing/`}>Upgrade to Pro Plus</Link> for adding more than one phone
                number
              </p>
            )}
            <Divider />

            <Form.Item noStyle shouldUpdate>
              {() => (
                <Button
                  variant="contained"
                  type="submit"
                  onClick={(data) => handleSubmit(onSubmit)(data)}
                  disabled={!isDirty || isLoading}
                >
                  Save Preferences
                </Button>
              )}
            </Form.Item>
          </Form>
        </Wrapper>
      )}
    </>
  );
};

export default AccountNotificationForm;
